import * as React from "react";
import {
    ProcessStage,
    ProcessStageArrow,
    ProcessStageArrowDirection,
    ProcessStageOrder,
    useProcessController
} from "../../Processes";
import {VeBoxCard} from "../../../components/card/VeBoxCard";
import {VeboxForm} from "../../../components/form/VeboxForm";
import {
    VeboxSelectionList,
    VeboxSelectionListOptionCheckboxPosition
} from "../../../components/selection_list/VeboxSelectionList";
import {InputAddon, Spinner, TextInput, Toggle} from "@vebox/etui-core";
import {generic_value, VeboxModels} from "../../../types/veboxModels";
import {Display} from "../../../components/Display";
import {FormCardFooter} from "../../common";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {
    getDifferentRole,
    getRoleFromRoleCode,
    getRolesAndPermissions,
    isIncludedInOrIncludesAtLeastOneRole,
    isRoleMatchingWord,
    RoleListOption,
    smartRoleSelection
} from "../../../components/Utils/RolesUtils";
import {UserCreationState, ValueToUpload} from "../UserCreation";
import {
    HierarchiesSection,
    mapHierarchiesSectionIntoUserHierarchy,
    mapUserHierarchyForRolesIntoString
} from "../../../users/HierarchyVisibilityUtils";
import {VeboxUser} from "../../../providers/VeboxUser";
import useCurrentUser = VeboxUser.useCurrentUser;

const ROLE_END_USER_ADMIN = "ROLE_END_USER_ADMIN";
const ROLE_ADMIN = "ROLE_ADMIN";

const ROLES_AND_PERMISSIONS_STEP_NUMBER = 3;

declare var AppTranslations: AppTranslations;

interface RolesAndPermissionsProps {
    onCreateUser: () => void;
    creationState: UserCreationState;
}

export const RolesAndPermissions: React.FC<RolesAndPermissionsProps> = ({creationState, onCreateUser}) => {

    const processController = useProcessController();
    const loggedInUser = useCurrentUser();

    const [roles, setRoles] = React.useState<VeboxModels.Role[]>([]);
    const [prevRolesSelected, setPrevRolesSelected] = React.useState<generic_value[]>([]);
    const [searchWord, setSearchWord] = React.useState("");

    const [selectedRoles, setSelectedRoles] = VeboxForm.useFormValueState<string[]>(ValueToUpload.ROLES);
    const [selectedHierarchies] = VeboxForm.useFormValueState<HierarchiesSection>(
        ValueToUpload.HIERARCHIES);

    const [, setDefaultInvisible] = VeboxForm.useFormValueState<boolean>(ValueToUpload.INVISIBLE_USER);

    const resetValues = () => {
        setSelectedRoles([])
        setPrevRolesSelected([])
    };

    const [isEndUserAdminChecked, setEndUserAdminCheck] = React.useState(false);
    const [isAdminChecked, setAdminCheck] = React.useState(false);
    const [isGettingRoles, setIsGettingRoles] = React.useState(false);

    const updateNormalSelectedRoles = (newRolesList) => {
        const rolesMngmt = roles.filter((role) => role.code !== ROLE_END_USER_ADMIN && role.code !== ROLE_ADMIN)
        setPrevRolesSelected(smartRoleSelection(
            getDifferentRole((newRolesList as string[]), (prevRolesSelected as string[])),
            (newRolesList as string[]),
            rolesMngmt));
    }

    const toggleCreationState = (e) => {
        if (e.target.checked) {
            setSelectedRoles([ROLE_END_USER_ADMIN]);
        } else {
            resetValues();
        }
    };

    const toggleAdminCreationState = (e) => {
        if (e.target.checked) {
            setSelectedRoles([ROLE_ADMIN]);
        } else if (isEndUserAdminChecked) {
            setSelectedRoles([ROLE_END_USER_ADMIN]);
        } else {
            resetValues();
        }
    };

    const clearSearchWord = () => setSearchWord("");

    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => setSearchWord(e.target.value);

    React.useEffect(() => {
        setSelectedRoles(prevRolesSelected as string[]);
    }, [prevRolesSelected])


    React.useEffect(() => {
        if (isAdminChecked) {
            setDefaultInvisible(true);
        } else {
            setDefaultInvisible(false);
        }
    }, [isAdminChecked]);

    React.useEffect(() => {
        if (selectedRoles?.find) {

            const hasEndUserAdmin = Boolean(selectedRoles?.find(r => r === ROLE_END_USER_ADMIN));
            const hasAdmin = Boolean(selectedRoles?.find(r => r === ROLE_ADMIN));

            setEndUserAdminCheck(hasEndUserAdmin || hasAdmin);
            setAdminCheck(hasAdmin);
        }

        if (processController.reachablePosition > ROLES_AND_PERMISSIONS_STEP_NUMBER - 1) {
            processController.setReachablePosition(ROLES_AND_PERMISSIONS_STEP_NUMBER - 1);
        }
    }, [selectedRoles]);


    React.useEffect(() => {
        resetValues();
        if (selectedHierarchies?.selectedHierarchies?.length > 0) {
            setIsGettingRoles(true);
            getRolesAndPermissions(
                mapUserHierarchyForRolesIntoString(mapHierarchiesSectionIntoUserHierarchy(selectedHierarchies),
                    loggedInUser.company.guid))
                .then((rolesAndPermissions) => {
                    setRoles(rolesAndPermissions)
                    setIsGettingRoles(false);
                })
                .catch(console.error);
        } else {
            setRoles([]);
            setIsGettingRoles(false);
        }
    }, [selectedHierarchies]);

    const renderLabelFactory = (ifTrue: string,
                                ifFalse?: string) => (isChecked: boolean) => isChecked ? ifTrue : (ifFalse ?? ifTrue);

    React.useEffect(() => {
        if (creationState === UserCreationState.INITIAL) {
            processController.setReachablePosition(0);
            setSearchWord("");
        }
    }, [creationState]);

    return (
        <ProcessStage
            stepNumber={ROLES_AND_PERMISSIONS_STEP_NUMBER}
            title={AppTranslations.userCreationProcess.rolesStageTitle}
            stageOrder={ProcessStageOrder.LAST}
        >
            <div className={"flx-col flx-align-center"}>
                <Display isVisible={creationState === UserCreationState.SUCCESSFUL}>
                    <div className={"success-creation-badge bold"}>
                        <span className="material-icons">task_alt</span>{AppTranslations.userCreationProcess.userCreatedSuccessfully}
                    </div>
                </Display>
                <div className={"flx-row"}>
                    <ProcessStageArrow
                        direction={ProcessStageArrowDirection.BACKWARD}
                        stepNumber={ROLES_AND_PERMISSIONS_STEP_NUMBER}
                    />
                    <VeBoxCard className={"form-card flx-col"} style={{height: "515px"}}>
                        <Display isVisible={isGettingRoles}>
                            <div className={"centered"} style={{width: "100%", height: 420}}>
                                <Spinner/>
                            </div>
                        </Display>
                        <Display isVisible={!isGettingRoles}>
                            <div className={"flx-col roles-container"}>
                                <TextInput
                                    label={AppTranslations.filters.filterBy}
                                    styles={{input: {boxSizing: "border-box"}}}
                                    className={"margin-bottom-10"}
                                    value={searchWord}
                                    onChange={changeSearchWord}
                                    endInputAddon={
                                        <InputAddon position={"end"}>
                                            {Boolean(searchWord) && <span
                                                className="material-icons-outlined clickable"
                                                onClick={clearSearchWord}
                                            >clear</span>}
                                        </InputAddon>
                                    }
                                />
                                <Display
                                    isVisible={roles
                                        .filter(isRoleMatchingWord(searchWord))
                                        .find(role => role.code === ROLE_END_USER_ADMIN)}
                                >
                                    <div className={"flx-row flx-align-center font-weight-400 padding-5"}>
                                        <Toggle
                                            name={"role_end_user_admin"}
                                            onChange={toggleCreationState}
                                            isChecked={isEndUserAdminChecked}
                                            renderLabels={renderLabelFactory(
                                                AppTranslations.userCreationProcess.enableAdminPermissions)}
                                        />
                                    </div>
                                </Display>
                                <Display
                                    isVisible={roles
                                        .filter(isRoleMatchingWord(searchWord))
                                        .find(role => role.code === ROLE_ADMIN) && isEndUserAdminChecked}
                                >
                                    <div className={"flx-row flx-align-center font-weight-400 padding-5"}>
                                        <Toggle
                                            name={"role_admin"}
                                            onChange={toggleAdminCreationState}
                                            isChecked={isAdminChecked}
                                            renderLabels={renderLabelFactory(
                                                AppTranslations.userCreationProcess.giveFullAccess)}
                                        />
                                    </div>
                                    <div className={"padding-5"} style={{color: "#777"}}>
                                        {AppTranslations.userCreationProcess.adminUserCreationWarning}
                                    </div>
                                </Display>
                                <VeBoxCard style={{margin: "10px 0", overflow: "auto"}}>
                                    <VeboxSelectionList
                                        className={"selection-list"}
                                        checkboxPosition={VeboxSelectionListOptionCheckboxPosition.START}
                                        disable={isEndUserAdminChecked}
                                        values={(selectedRoles ?? [])}
                                        onChange={updateNormalSelectedRoles}
                                    >
                                        {roles
                                            .filter(
                                                (role) => role.code !== ROLE_END_USER_ADMIN && role.code !== ROLE_ADMIN)
                                            .filter(isRoleMatchingWord(searchWord))
                                            .sort((a, b) => a.name > b.name ? 1 : -1)
                                            .map((role) => {
                                                const isDisabled: boolean = isIncludedInOrIncludesAtLeastOneRole(
                                                    role,
                                                    (selectedRoles ?? [])
                                                        .filter((r: string) => r !== role.code)
                                                        .map((r) => getRoleFromRoleCode(r as string, roles)),
                                                    true
                                                );
                                                return <RoleListOption
                                                    role={role}
                                                    key={role.code}
                                                    disabledMessage={AppTranslations.usersManagement.roleContainedInAnother}
                                                    disable={isDisabled}
                                                />
                                            })
                                        }
                                    </VeboxSelectionList>
                                </VeBoxCard>
                            </div>
                            <FormCardFooter
                                nextButtonText={"Create user"}
                                onReset={resetValues}
                                onNext={onCreateUser}
                                disabled={(selectedRoles ?? []).length === 0}
                                loadable={true}
                                loading={creationState === UserCreationState.PENDING}
                            />
                        </Display>
                    </VeBoxCard>
                    <ProcessStageArrow
                        visible={(selectedRoles ?? []).length > 0 && (processController.reachablePosition > ROLES_AND_PERMISSIONS_STEP_NUMBER - 1)}
                        direction={ProcessStageArrowDirection.FORWARD}
                        stepNumber={ROLES_AND_PERMISSIONS_STEP_NUMBER}
                    />
                </div>
            </div>
        </ProcessStage>
    );
}
